import { useDispatch, useSelector } from 'react-redux';
import React from 'react';
import { CircleArrowUp } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { Button } from '@/components/ui/button';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';

import { RootState } from '../../../reducers';
import { usePlan } from '../../plan/hooks/plan';
import { pushModal } from '../../globalModals/globalModalsSlice';

const UpsellButton = ({ selected }: { selected?: 'annual' | 'unlimited' }) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const [isSmallScreen, setIsSmallScreen] = React.useState(false);

  React.useEffect(() => {
    const checkScreenSize = () => {
      setIsSmallScreen(window.innerWidth < 768);
    };
    checkScreenSize();
    window.addEventListener('resize', checkScreenSize);
    return () => window.removeEventListener('resize', checkScreenSize);
  }, []);

  const { isPlan } = usePlan();
  if (isPlan(['ENTERPRISE'])) {
    return null;
  }

  const { term } = useSelector((state: RootState) => state.plan.subscription);
  const showGoAnnual =
    selected !== 'unlimited' &&
    (selected === 'annual' ||
      (isPlan(['GROWTH', 'BUSINESS', 'ENTERPRISE']) &&
        (term === 'monthly' || term === 'weekly')));

  const showGoUnlimited =
    selected === 'unlimited' || (!showGoAnnual && isPlan(['GROWTH']));

  const handleClick = () => {
    dispatch(pushModal({ type: 'upsell-modal' }));
  };

  if (!showGoAnnual && !showGoUnlimited) {
    return null;
  }

  return (
    <TooltipProvider>
      <>
        {showGoAnnual && (
          <Tooltip>
            <TooltipTrigger asChild>
              {isSmallScreen ? (
                <Button
                  variant="default"
                  size="icon"
                  onClick={handleClick}
                  className="h-10 w-10"
                >
                  <CircleArrowUp className="h-4 w-4" />
                </Button>
              ) : (
                <Button
                  variant="default"
                  onClick={handleClick}
                  className="h-10 px-3 shadow-sm transition-all duration-300 ease-in-out"
                >
                  <CircleArrowUp className="mr-1 h-4 w-4" />
                  {t('upgrade.buttons.saveMoney')}
                </Button>
              )}
            </TooltipTrigger>
            <TooltipContent>
              <p>{t('upgrade.tooltips.annual')}</p>
            </TooltipContent>
          </Tooltip>
        )}
        {showGoUnlimited && (
          <Tooltip>
            <TooltipTrigger asChild>
              {isSmallScreen ? (
                <Button
                  variant="default"
                  size="icon"
                  onClick={handleClick}
                  className="h-10 w-10"
                >
                  <CircleArrowUp className="h-4 w-4" />
                </Button>
              ) : (
                <Button
                  variant="default"
                  onClick={handleClick}
                  className="h-10 px-3 shadow-sm transition-all duration-300 ease-in-out"
                >
                  <CircleArrowUp className="mr-1 h-4 w-4" />
                  {t('upgrade.buttons.goUnlimited')}
                </Button>
              )}
            </TooltipTrigger>
            <TooltipContent>
              <p>{t('upgrade.tooltips.unlimited')}</p>
            </TooltipContent>
          </Tooltip>
        )}
      </>
    </TooltipProvider>
  );
};

export default UpsellButton;
